import * as React from 'react';
import { atom, useRecoilValue, useSetRecoilState } from 'recoil';

import { createPalette, usePaletteHandle } from '@gitbook/react-uikit';
import { BlockRange } from '@gitbook/slate';
import { useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';

import { useUpgrade } from '../../../state';
import { useReusableContentPaletteGroup } from './palette';

const HAS_LOCAL_STORAGE = typeof window !== 'undefined' && 'localStorage' in window;
const REUSABLE_CONTENT_BLOCK_COPY_DISCLAIMER_UPGRADE_DISMISSED =
    'ReusableContentSuggestionPromptUpgrade.dismissed';

export function useSetDisclaimer() {
    return useSetRecoilState(disclaimerState);
}

/**
 * A prompt that suggests the user to turn a block into reusable content when copying.
 */
export const ReusableContentSuggestionPrompt = React.memo(
    function ReusableContentSuggestionPrompt(props: { nodeKey: string }) {
        const editor = useEditor();
        const blockRange = BlockRange.isBlockRange(editor.value.selection)
            ? editor.value.selection
            : null;
        const disclaimer = useRecoilValue(disclaimerState);
        const hasDisclaimer = disclaimer === props.nodeKey;
        if (!hasDisclaimer || !blockRange) {
            return null;
        }
        return <VisiblePrompt nodeKey={props.nodeKey} />;
    }
);

function VisiblePrompt(props: { nodeKey: string }) {
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const createPaletteGroup = useReusableContentPaletteGroup({
        nodeKey: props.nodeKey,
        strict: true,
        method: 'editor-prompt-on-copy',
    });
    const upgrade = useUpgrade('reusable-content');
    const hasUpgrade = Boolean(upgrade);
    const setDisclaimer = useSetDisclaimer();
    // If the user is on a free plan, only show the disclaimer once. Otherwise, show every time.
    const shouldShowDisclaimer = hasUpgrade ? checkShouldShowUpgradeDisclaimer() : true;
    const { open: openPalette } = usePaletteHandle(anchorRef, {
        onClose: () => setDisclaimer(null),
    });
    React.useEffect(() => {
        const paletteGroup = createPaletteGroup();

        if (!paletteGroup || !shouldShowDisclaimer) {
            return;
        }

        openPalette(
            createPalette({
                position: {
                    preferredPlacement: 'left',
                },
                groups: [
                    paletteGroup,
                    {
                        key: 'dismiss',
                        commands: [
                            hasUpgrade
                                ? {
                                      key: 'dismiss',
                                      title: 'Don’t tell me this again',
                                      icon: Icon.X,
                                      trigger: () => {
                                          doNotShowDisclaimerAgain();
                                          setDisclaimer(null);
                                      },
                                  }
                                : {
                                      key: 'dismiss',
                                      title: 'Dismiss',
                                      icon: Icon.X,
                                      trigger: () => setDisclaimer(null),
                                  },
                        ],
                    },
                ],
            })
        );
    }, [openPalette, hasUpgrade, setDisclaimer, shouldShowDisclaimer, createPaletteGroup]);
    if (!shouldShowDisclaimer) {
        return null;
    }
    return (
        <div
            ref={anchorRef}
            style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
            }}
        />
    );
}

function checkShouldShowUpgradeDisclaimer(): boolean {
    if (HAS_LOCAL_STORAGE) {
        return (
            localStorage.getItem(REUSABLE_CONTENT_BLOCK_COPY_DISCLAIMER_UPGRADE_DISMISSED) !==
            'true'
        );
    }
    return false;
}

function doNotShowDisclaimerAgain() {
    if (HAS_LOCAL_STORAGE) {
        localStorage.setItem(REUSABLE_CONTENT_BLOCK_COPY_DISCLAIMER_UPGRADE_DISMISSED, 'true');
    }
}

const disclaimerState = atom<string | null>({
    key: 'ReusableContentSuggestionPrompt',
    default: null,
});
